import { isCompletedMonth } from '@app/purchased-goods/insights/lib/dense-series';
import { InsightsNoSpendData } from '@app/purchased-goods/insights/components/insights-no-spend-data';
import { PurchasedGoodItemInsights } from '@shared/api/types';
import SimpleAreaChart from '@shared/components/chart/simple-area-chart';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import SimpleSelect from '@shared/components/simple-select';
import { Card } from '@shared/components/ui/card';
import { ChartConfig } from '@shared/components/ui/chart';
import { formatCost, formatTonnes } from '@shared/lib/format';
import { cn } from '@shared/lib/utils';
import { format, parseISO } from 'date-fns';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useMemo } from 'react';

const CHART_TYPES = ['emissions', 'spend'] as const;
type ItemChartType = (typeof CHART_TYPES)[number];

const CHART_LABELS: Record<ItemChartType, string> = {
  emissions: 'Emissions over time',
  spend: 'Spend over time',
};

const PREVIOUS_YEAR_COLOR = 'var(--muted-foreground)';

type Props = {
  data?: PurchasedGoodItemInsights;
  currency?: string | null;
  /** Whether spend is available; false → the spend chart shows an empty state. */
  hasSpendData: boolean;
  /** Without the cost feature the spend chart stays listed but locked. */
  hasCostFeature: boolean;
  className?: string;
};

/**
 * The item's monthly emissions or spend for the selected scope, with a dashed
 * previous-year comparison line.
 */
export function ItemOverTimeSection({
  data,
  currency,
  hasSpendData,
  hasCostFeature,
  className,
}: Props) {
  const [chartType, setChartType] = useQueryState(
    'chart',
    parseAsStringLiteral(CHART_TYPES).withDefault('emissions')
  );
  const activeType: ItemChartType =
    chartType === 'spend' && hasCostFeature ? 'spend' : 'emissions';
  const isSpend = activeType === 'spend';

  // The series is dense (one point per month of the window). Completed months
  // with no purchases plot as zero; months still ahead stay unplotted so the
  // line ends at "now" while the axis spans the whole window. A completed
  // month with activity but no spend value is missing data, not zero spend,
  // so it stays a gap on the spend chart.
  const chartData = useMemo(
    () =>
      [...(data?.emissionsOverTime ?? [])]
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((point) => {
          const noActivity = point.emissionsTonnes == null;
          const zeroWhenCompleted =
            noActivity && isCompletedMonth(point.date) ? 0 : null;
          return {
            label: format(parseISO(point.date), 'MMM yy'),
            emissions: point.emissionsTonnes ?? zeroWhenCompleted,
            emissionsPreviousYear: point.emissionsTonnesPreviousYear,
            spend:
              point.spend != null ? Number(point.spend) : zeroWhenCompleted,
            spendPreviousYear:
              point.spendPreviousYear != null
                ? Number(point.spendPreviousYear)
                : null,
          };
        }),
    [data?.emissionsOverTime]
  );

  const chartConfig: ChartConfig = isSpend
    ? {
        spend: { label: 'This period', color: 'var(--spend-400)' },
        spendPreviousYear: {
          label: 'Previous year',
          color: PREVIOUS_YEAR_COLOR,
        },
      }
    : {
        emissions: {
          label: 'This period',
          color: 'var(--chart-food-and-drink)',
        },
        emissionsPreviousYear: {
          label: 'Previous year',
          color: PREVIOUS_YEAR_COLOR,
        },
      };

  return (
    <Card className={cn('flex flex-col p-5 pt-3', className)}>
      <div className="mb-4 flex min-h-10 items-center justify-between gap-2">
        <Text variant="cardTitle">{CHART_LABELS[activeType]}</Text>
        <SimpleSelect
          className="w-52"
          options={CHART_TYPES.map((key) => {
            const locked = key === 'spend' && !hasCostFeature;
            return {
              value: CHART_LABELS[key],
              disabled: locked,
              icon: locked ? (
                <Icons.lock className="size-3.5 text-gray-400" />
              ) : undefined,
            };
          })}
          value={CHART_LABELS[activeType]}
          onValueChange={(label) => {
            const next =
              CHART_TYPES.find((key) => CHART_LABELS[key] === label) ??
              'emissions';
            void setChartType(next);
          }}
        />
      </div>
      {isSpend && !hasSpendData ? (
        <InsightsNoSpendData />
      ) : (
        <div className="h-80 w-full flex-1">
          <SimpleAreaChart
            chartConfig={chartConfig}
            chartData={chartData}
            className="aspect-auto size-full"
            nameKey="label"
            dataKey={isSpend ? 'spend' : 'emissions'}
            dashedKeys={
              isSpend ? ['spendPreviousYear'] : ['emissionsPreviousYear']
            }
            strokeWidth={2}
            fillOpacity={0.25}
            margin={{ top: 8, right: 24, left: 24, bottom: 0 }}
            // Cap the axis at ~8 evenly spaced month labels rather than
            // letting the chart drop random ticks to fit.
            XAxisProps={{
              interval: Math.max(0, Math.ceil(chartData.length / 8) - 1),
            }}
            // Values live in the hover tooltip; the chart itself stays visual.
            YAxisProps={{ hide: true }}
            tooltipValueFormatter={(value) =>
              isSpend
                ? formatCost(Number(value), currency)
                : formatTonnes(Number(value))
            }
          />
        </div>
      )}
    </Card>
  );
}
